import { type State } from './address.js';
import { type BaseItem, type Nullable, type NullablePartial, type TimingUnit } from './common.js';
import { type AppointmentStatus } from './encounter.js';
import { type Kind as OrderKind, type OrderParameter } from './order.js';
import { Kind as GuarantorKind } from './patient/guarantor.js';
import { type EligibilityStatus } from './patient/insuranceInformation.js';
import { type AuthorizationStatus } from './patient/order.js';
import { type Sex } from './patient.js';
import type * as User from './user.js';

export { GuarantorKind };

export type FindAllOptions = Partial<{
  name: string;
  nameSubstring: string;
  output: OutputKind;
  outputIncludes: string;
  providerId: string;
  practiceId: string;
  scenarioPostVisitId: string;
  limit: number;
  page: number;
  startTime: string;
  organizationId: string;
  entity: Entity;
  isEnabled: boolean;
}>;

export type FindAllOptionsBasic = Partial<{
  organizationId: string;
  q: string;
  limit: number;
}>;

export enum OutputKind {
  Authorization = 'AUTHORIZATION',
  Flags = 'FLAGS',
  Flows = 'FLOWS',
  Forms = 'FORMS',
  Jobs = 'JOBS',
  NoteTemplates = 'NOTE_TEMPLATES',
  Notifications = 'NOTIFICATIONS',
  PostVisits = 'POST_VISITS',
  PreRegistrations = 'PRE_REGISTRATIONS',
  ProblemGroups = 'PROBLEM_GROUPS',
  RemoveFeedItems = 'REMOVE_FEED_ITEMS',
}

export enum ApplyOnceUnit {
  CalendarYear = 'CALENDAR_YEAR',
  Year = 'YEAR',
  CalendarMonth = 'CALENDAR_MONTH',
  Month = 'MONTH',
  CalendarWeek = 'CALENDAR_WEEK',
  LocalDay = 'LOCAL_DAY',
  Day = 'DAY',
  Hour = 'HOUR',
}

export enum ApplyOnceEntity {
  EhrLogin = 'EHR_LOGIN',
  Encounter = 'ENCOUNTER',
  Order = 'ORDER',
  PrevisitDocumentTemplate = 'PREVISIT_DOCUMENT_TEMPLATE',
  PrevisitFormTemplate = 'PREVISIT_FORM_TEMPLATE',
  Visit = 'VISIT',
}

export enum ApplyOnceStatus {
  Completed = 'COMPLETED',
  Created = 'CREATED',
  Received = 'RECEIVED',
  Started = 'STARTED',
  Updated = 'UPDATED',
}

export enum ApplyOncePracticeType {
  Any = 'ANY',
  Same = 'SAME',
  Chosen = 'CHOSEN',
}

export interface ApplyOnceFilter {
  duration: number;
  unit: ApplyOnceUnit;
  entityKind: ApplyOnceEntity;
  entityStatus: ApplyOnceStatus | null;
  entityId: string | null;
  negated: boolean | null;
  practiceType: ApplyOncePracticeType | null;
  practiceIds: string[] | null;
}

export enum Entity {
  Encounter = 'ENCOUNTER',
  Order = 'ORDER',
  Patient = 'PATIENT',
}

export interface PrevisitData {
  [formTemplateName: string]: {
    [questionName: string]: string | string[];
  };
}

export enum Event {
  EncounterCreated = 'ENCOUNTER_CREATED',
  FormSetCreated = 'FORM_SET_CREATED',
  FormSetCompleted = 'FORM_SET_COMPLETED',
  FormSetExpired = 'FORM_SET_EXPIRED',
  FormSetStarted = 'FORM_SET_STARTED',
  NoticeOfAdmissionNew = 'NOTICE_OF_ADMISSION_NEW',
  NoticeOfAdmissionPending = 'NOTICE_OF_ADMISSION_PENDING',
  NoticeOfAdmissionApproved = 'NOTICE_OF_ADMISSION_APPROVED',
  NoticeOfAdmissionException = 'NOTICE_OF_ADMISSION_EXCEPTION',
  NoticeOfAdmissionDenied = 'NOTICE_OF_ADMISSION_DENIED',
  PatientCreated = 'PATIENT_CREATED',
  PatientCheckedIn = 'PATIENT_CHECKED_IN',
  PatientOrderUnknown = 'PATIENT_ORDER_UNKNOWN',
  PatientOrderNew = 'PATIENT_ORDER_NEW',
  PatientOrderProcessingSubmission = 'PATIENT_ORDER_PROCESSING_SUBMISSION',
  PatientOrderProcessedApproved = 'PATIENT_ORDER_PROCESSED_APPROVED',
  PatientOrderProcessedException = 'PATIENT_ORDER_PROCESSED_EXCEPTION',
  PatientOrderProcessedMixedDetermination = 'PATIENT_ORDER_PROCESSED_MIXED_DETERMINATION',
  PatientOrderProcessedNotRequired = 'PATIENT_ORDER_PROCESSED_NOT_REQUIRED',
  PatientOrderProcessedPending = 'PATIENT_ORDER_PROCESSED_PENDING',
  PatientOrderProcessedRejected = 'PATIENT_ORDER_PROCESSED_REJECTED',
  PatientOrderApproved = 'PATIENT_ORDER_APPROVED',
  PatientOrderException = 'PATIENT_ORDER_EXCEPTION',
  PatientOrderMixedDetermination = 'PATIENT_ORDER_MIXED_DETERMINATION',
  PatientOrderNotRequired = 'PATIENT_ORDER_NOT_REQUIRED',
  PatientOrderPending = 'PATIENT_ORDER_PENDING',
  PatientOrderRejected = 'PATIENT_ORDER_REJECTED',
  PatientOrderNewPending = 'PATIENT_ORDER_NEW_PENDING',
  PatientOrderProcessingStatusing = 'PATIENT_ORDER_PROCESSING_STATUSING',
  PatientOrderStatusingPending = 'PATIENT_ORDER_STATUSING_PENDING',
  PatientOrderManualReviewRequired = 'PATIENT_ORDER_MANUAL_REVIEW_REQUIRED',
}

export enum WindowTimes {
  '00:00' = '00:00',
  '01:00' = '01:00',
  '02:00' = '02:00',
  '03:00' = '03:00',
  '04:00' = '04:00',
  '05:00' = '05:00',
  '06:00' = '06:00',
  '07:00' = '07:00',
  '08:00' = '08:00',
  '09:00' = '09:00',
  '10:00' = '10:00',
  '11:00' = '11:00',
  '12:00' = '12:00',
  '13:00' = '13:00',
  '14:00' = '14:00',
  '15:00' = '15:00',
  '16:00' = '16:00',
  '17:00' = '17:00',
  '18:00' = '18:00',
  '19:00' = '19:00',
  '20:00' = '20:00',
  '21:00' = '21:00',
  '22:00' = '22:00',
  '23:00' = '23:00',
}

export type ScenarioRule = { isEnabled: boolean } & Nullable<{
  entity: Entity;
  applicableGuarantorKinds: GuarantorKind[];
  applicablePracticeIds: string[];
  excludedPracticeIds: string[];
  applicablePracticeGroupIds: string[];
  excludedPracticeGroupIds: string[];
  exactProcedureNames: string[];
  applicableOrganizationIds: string[];
  applicablePrevisitLinkIds: string[];
  applicableProviderIds: string[];
  excludedProviderIds: string[];
  applicableProviderKinds: User.Kind[];
  applicableFeedIds: string[];
  specialty: string; // DEPRECATED
  applicableSpecialties: string[];
  excludedSpecialties: string[];
  sex: Sex;
  ageLowerBound: number;
  ageUpperBound: number;
  ageDayLowerBound: number;
  ageDayUpperBound: number;
  ageWeekLowerBound: number;
  ageWeekUpperBound: number;
  ageMonthLowerBound: number;
  ageMonthUpperBound: number;
  applicableInsuranceProviders: string[];
  excludedInsuranceProviders: string[];
  applicablePrimaryPayors: string[];
  excludedPrimaryPayors: string[];
  applicableSecondaryPayors: (string | null)[];
  excludedSecondaryPayors: string[];
  applicablePrimaryPayorPlanNames: string[];
  excludedPrimaryPayorPlanNames: string[];
  applicableSecondaryPayorPlanNames: string[];
  excludedSecondaryPayorPlanNames: string[];
  applicableProblems: string[];
  applicableIcd10Prefixes: string[];
  applicableVisitReasons: string[];
  excludedVisitReasons: string[];
  applicableKindOfVisits: string[];
  excludedKindOfVisits: string[];
  applicableAppointmentNotes: string[];
  applicableReferredByProviderIds: string[];
  excludedReferredByProviderIds: string[];
  applicablePrimaryCareProviderNames: string[];
  excludedPrimaryCareProviderNames: string[];
  applicableCareProviderNpis: string[];
  excludedCareProviderNpis: string[];
  excludedAppointmentNotes: string[];
  applicableProcedureNames: string[];
  allowedProcedureNames: string[];
  excludedProcedureNames: string[];
  hasPayment: boolean;
  hasGuarantor: boolean;
  previsit: PrevisitData;
  applyOnceFilters: ApplyOnceFilter[];
  scenarioPostVisitId: string;
  startTimeAfter: Date;
  startTimeAfterRelativeDuration: number;
  startTimeAfterRelativeUnit: TimingUnit;
  startTimeBefore: Date;
  startTimeBeforeRelativeDuration: number;
  startTimeBeforeRelativeUnit: TimingUnit;
  enrolledInEhrPortal: boolean;
  applicableHomeMarkets: string[];
  applicableSchedulingSources: string[];
  requiredCompletedEvents: Event[];
  requiredCompletedJobKinds: Job[];
  requiredSuccessfullyCompletedJobKinds: Job[];
  applicableUnsuccessfullyCompletedJobKind: Job;
  isValueBasedCare: boolean;
  applicablePatientAdvisories: string[];
  hasPatientAdvisories: boolean;
  applicableUserAgents: string[];
  excludedUserAgents: string[];
  preregistrationCompletedExternally: boolean;
  applicableAppointmentStatuses: AppointmentStatus[];
  excludedAppointmentStatuses: AppointmentStatus[];
  applicableEligibilityStatuses: EligibilityStatus[];
  eligibilitySubmitDateWithinRelativeDuration: number;
  eligibilitySubmitDateWithinRelativeUnit: TimingUnit;
  coverageEndDateAfterRelativeDuration: number;
  coverageEndDateAfterRelativeUnit: TimingUnit;
  coverageStartDateBeforeRelativeDuration: number;
  coverageStartDateBeforeRelativeUnit: TimingUnit;
  applicableReferredToDepartments: string[];
  excludedReferredToDepartments: string[];
  applicablePatientAddressMailingStates: State[];
}>;

export type ScenarioPopulations = Nullable<{
  requiredPopulationIds: string[];
  excludedPopulationIds: string[];
}>;

export interface Order {
  kind: OrderKind;
  icd10s: string[];
  name: string;
  facilityName?: string;
  parameters?: OrderParameter[];
}

export interface ProblemGroup {
  problem: string;
  icd10s: string[];
  orders: Order[];
}

export enum PostVisitUnit {
  Year = 'YEAR',
  Month = 'MONTH',
  Week = 'WEEK',
  Day = 'DAY',
}

export interface PostVisit {
  id?: string;
  unit: PostVisitUnit;
  duration: number;
}

// Combine with PostVisit after confirming all contexts accept the same shape
export interface PostVisitOutput {
  id?: string;
  unit: PostVisitUnit;
  duration: number;
  scenarioId?: string;
  forms?: Form[];
}

export interface Form {
  id?: string;
  add: boolean;
  templateId: string;
}

export enum Job {
  ExportAuthorizationForm = 'EXPORT_AUTHORIZATION_FORM',
  ExportAuthorizationStart = 'EXPORT_AUTHORIZATION_START',
  ExportAuthorizationStatus = 'EXPORT_AUTHORIZATION_STATUS',
  ExportAuthorizationSubmission = 'EXPORT_AUTHORIZATION_SUBMISSION',
  ExportCareGapFindings = 'EXPORT_CARE_GAP_FINDINGS',
  ExportNewEncounter = 'EXPORT_NEW_ENCOUNTER',
  ExportEncounterInformation = 'EXPORT_ENCOUNTER_INFORMATION',
  ExportGuarantors = 'EXPORT_GUARANTORS',
  ExportHccFindings = 'EXPORT_HCC_FINDINGS',
  ExportPayorAuthorization = 'EXPORT_PAYOR_AUTHORIZATION',
  ExportPreregistrationEncounter = 'EXPORT_PREREGISTRATION_ENCOUNTER',
  ExportPrevisitPlanning = 'EXPORT_PREVISIT_PLANNING',
  ExportReviewClinicalAdvisories = 'EXPORT_REVIEW_CLINICAL_ADVISORIES',
  ExportTerminatedInsurances = 'EXPORT_TERMINATED_INSURANCES',
  ExportVerifyRegistration = 'EXPORT_VERIFY_REGISTRATION',
  ExtractAllergies = 'EXTRACT_ALLERGIES',
  ExtractAuthorizationStatus = 'EXTRACT_AUTHORIZATION_STATUS',
  ExtractCareGapFindings = 'EXTRACT_CARE_GAP_FINDINGS',
  ExtractCareGapStatuses = 'EXTRACT_CARE_GAP_STATUSES',
  ExtractEncounterInformation = 'EXTRACT_ENCOUNTER_INFORMATION',
  ExtractFamilyMedicalHistory = 'EXTRACT_FAMILY_MEDICAL_HISTORY',
  ExportOrderRequestListStatus = 'EXPORT_ORDER_REQUEST_LIST_STATUS',
  ExtractGuarantors = 'EXTRACT_GUARANTORS',
  ExtractHistoricalPatientEncounters = 'EXTRACT_HISTORICAL_PATIENT_ENCOUNTERS',
  ExtractLabValues = 'EXTRACT_LAB_VALUES',
  ExtractMedicalNecessity = 'EXTRACT_MEDICAL_NECESSITY',
  ExtractMedications = 'EXTRACT_MEDICATIONS',
  ExtractMspqForm = 'EXTRACT_MSPQ_FORM',
  ExtractOutstandingBalances = 'EXTRACT_OUTSTANDING_BALANCES',
  ExtractPatientAdvisories = 'EXTRACT_PATIENT_ADVISORIES',
  ExtractPatientAdvisoryDocuments = 'EXTRACT_PATIENT_ADVISORY_DOCUMENTS',
  ExtractPatientCareProviders = 'EXTRACT_PATIENT_CARE_PROVIDERS',
  ExtractPatientCommunicationConsent = 'EXTRACT_PATIENT_COMMUNICATION_CONSENT',
  ExtractPatientDocuments = 'EXTRACT_PATIENT_DOCUMENTS',
  ExtractImmunizationRegistry = 'EXTRACT_IMMUNIZATION_REGISTRY',
  ExtractPatientInformation = 'EXTRACT_PATIENT_INFORMATION',
  ExtractPatientMedicalHistory = 'EXTRACT_PATIENT_MEDICAL_HISTORY',
  ExtractPatientOrder = 'EXTRACT_PATIENT_ORDER',
  ExtractPatientPharmacies = 'EXTRACT_PATIENT_PHARMACIES',
  ExtractPatientProblems = 'EXTRACT_PATIENT_PROBLEMS',
  ExtractSocialHistory = 'EXTRACT_SOCIAL_HISTORY',
  ExtractSurgicalHistory = 'EXTRACT_SURGICAL_HISTORY',
  ExtractUpcomingPatientInsurance = 'EXTRACT_UPCOMING_PATIENT_INSURANCE',
  ExtractUpcomingPregnancy = 'EXTRACT_UPCOMING_PREGNANCY',
}

type ScenarioTimings = Nullable<{
  encounterStartTimeRelativeDuration: number;
  encounterStartTimeRelativeUnit: TimingUnit;
}>;

type ScenarioOutputs = Nullable<{
  authorizationNote: string;
  authorizationStatus: AuthorizationStatus;
  flowDefinitionIds: string[];
  flowApplyOnce: boolean;
  flowApplyOnceRelativeDuration: number;
  flowApplyOnceRelativeUnit: TimingUnit;
  allowUnpublishedFlowRun: boolean;
  forms: Form[];
  ingestJobKinds: Job[];
  noteTemplateId: string;
  notificationContentIds: string[];
  postVisits: PostVisitOutput[];
  problemGroups: ProblemGroup[];
  removeFeedItems: string[];
  flags: string[];
}>;

interface ScenarioIdentifier {
  name: string;
  description: string;
  organizationId: string | null;
}

export interface BaseScenario
  extends
    ScenarioRule,
    ScenarioPopulations,
    ScenarioTimings,
    ScenarioWindow,
    ScenarioOutputs,
    ScenarioIdentifier {}

type ScenarioWindow = Nullable<{
  windowStartTime: string;
  windowEndTime: string;
}>;

export interface Scenario extends BaseItem, BaseScenario {}
export type ScenarioBasic = Pick<Scenario, keyof BaseItem | keyof ScenarioIdentifier>;

export interface HistoryEvent {
  id: string;
  created: string;
  userId: string;
  scenarioId: string;
  scenario: Scenario;
}

export type CreateValues = NullablePartial<BaseScenario>;

export function parse(scenario: Scenario): Scenario {
  return {
    ...scenario,
    created: scenario.created,
    updated: scenario.updated,
    startTimeAfter: parseDate(scenario.startTimeAfter),
    startTimeBefore: parseDate(scenario.startTimeBefore),
  };
}

function parseDate(value: Date | string | null): Date | null {
  return value ? new Date(value) : null;
}
